import React, { useMemo, useState, useEffect } from "react";
import { Stack } from "@mui/material";
import { IoMdArrowDropup, IoMdArrowDropdown } from "react-icons/io";

/* ================= Helpers ================= */
const toArray = (v) => (Array.isArray(v) ? v : v ? [v] : []);
const safe = (v, fb = "-") => (v === null || v === undefined || v === "" ? fb : v);
const num = (v) => (typeof v === "number" ? v : v ? Number(v) : 0);

const parseAnyDate = (d) => {
  if (!d) return null;
  if (d instanceof Date) return d;
  if (typeof d !== "string") return null;
  if (d.includes("T")) {
    const x = new Date(d);
    return isNaN(x) ? null : x;
  }
  const [datePart, timePart] = d.split(" ");
  if (datePart && datePart.includes("/")) {
    const [dd, mm, yyyy] = datePart.split("/");
    const x = new Date(`${yyyy}-${mm}-${dd}${timePart ? `T${timePart}` : ""}`);
    return isNaN(x) ? null : x;
  }
  const x = new Date(d);
  return isNaN(x) ? null : x;
};

const fmtDate = (d) => {
  const x = parseAnyDate(d);
  if (!x) return "-";
  const dd = String(x.getDate()).padStart(2, "0");
  const mm = String(x.getMonth() + 1).padStart(2, "0");
  const yy = x.getFullYear();
  return `${yy}/${mm}/${dd}`;
};

/* ================= Composant ================= */
export default function ClientClaimsDataTable({ claims, contracts, client }) {
  const td1Headers = ["Police", "Pack", "Immat.", "Usage", "Prime Nette", "Date d'effet"];
  const td2Headers = [
    "Numéro Sinistre",
    "Date Survenance",
    "Compagnie Adverse",
    "Type Sinistre",
    "Réglements",
    "Statut",
    "Date Réglement",
  ];

  // On récupère au mieux l’UID client
  const clientUid =
    client?.partyUID || client?.partyUid || client?.codeClient || client?.uid || null;

  const rows = useMemo(() => {
    const contractsArr = Array.isArray(contracts) ? contracts : [];
    const claimsArr = Array.isArray(claims) ? claims : [];

    // Tous les sinistres "du client" via partyUid
    const clientClaims = claimsArr.filter((cl) =>
      toArray(cl?.involvedParties)
        .map((p) => String(p?.partyUid || ""))
        .includes(String(clientUid || ""))
    );

    // Cas simple : un seul contrat → on affiche tous les sinistres du client (ou tous les sinistres si pas d’UID)
    if (contractsArr.length === 1) {
      return [
        {
          contract: contractsArr[0],
          relatedClaims: (clientUid ? clientClaims : claimsArr).slice().sort((a, b) => {
            const da =
              parseAnyDate(a?.occurrenceDate || a?.reportingDate || a?.openingDate)?.getTime() ||
              0;
            const db =
              parseAnyDate(b?.occurrenceDate || b?.reportingDate || b?.openingDate)?.getTime() ||
              0;
            return db - da;
          }),
        },
      ];
    }

    // Sinon : jointure large + garde-fou
    return contractsArr.map((contract) => {
      const policyNumber =
        contract?.policyNumber ||
        contract?.numeroContrat ||
        contract?.policy ||
        contract?.numeroPolice ||
        "";

      const contractGoodUids = toArray(contract?.good).map((g) => g?.uid).filter(Boolean);
      const policyHolder = safe(contract?.policyHolder, "");

      let related = claimsArr.filter((cl) => {
        const claimPolicyUids = toArray(cl?.involvedPolicies)
          .map((p) => p?.policyUid)
          .filter(Boolean)
          .map(String);
        const claimGoodUids = toArray(cl?.involvedCars)
          .map((c) => c?.goodUid)
          .filter(Boolean)
          .map(String);
        const claimPartyUids = toArray(cl?.involvedParties)
          .map((p) => p?.partyUid)
          .filter(Boolean)
          .map(String);

        const byPolicyNumber =
          policyNumber &&
          claimPolicyUids.some((u) => String(u).trim() === String(policyNumber).trim());

        const byGoodUid =
          contractGoodUids.length > 0 &&
          claimGoodUids.some((u) => contractGoodUids.includes(String(u)));

        const byHolder =
          policyHolder && claimPartyUids.some((u) => String(u) === String(policyHolder));

        const byClientUid =
          clientUid && claimPartyUids.some((u) => String(u) === String(clientUid));

        return byPolicyNumber || byGoodUid || byHolder || byClientUid;
      });

      // Garde-fou: si on a très peu de match alors qu’on a beaucoup de sinistres client,
      // élargir au set client pour éviter “1 seule ligne”.
      if (clientUid && clientClaims.length > 5 && related.length <= 1) {
        related = clientClaims;
      }

      // Dernier filet : si rien, on affiche tout de même les sinistres (pour ne pas laisser vide)
      if (related.length === 0) {
        related = claimsArr;
      }

      // Tri par date décroissante
      related = related.slice().sort((a, b) => {
        const da =
          parseAnyDate(a?.occurrenceDate || a?.reportingDate || a?.openingDate)?.getTime() ||
          0;
        const db =
          parseAnyDate(b?.occurrenceDate || b?.reportingDate || b?.openingDate)?.getTime() ||
          0;
        return db - da;
      });

      return { contract, relatedClaims: related };
    });
  }, [claims, contracts, clientUid]);

  const [dataTable, setDataTable] = useState(rows);
  useEffect(() => setDataTable(rows), [rows]);

  /* ====== Getters contrat ====== */
  const getContractField = (c, key) => {
    switch (key) {
      case "Police":
        return c?.policyNumber || c?.numeroContrat || c?.policy || "-";
      case "Pack":
        return safe(c?.policyType);
      case "Immat.":
        return toArray(c?.good)[0]?.registrationNumber || toArray(c?.good)[0]?.immat || "-";
      case "Usage":
        return safe(c?.policyUsage);
      case "Prime Nette":
        return "-"; // non fourni
      case "Date d'effet":
        return fmtDate(c?.subscriptionDate || c?.dateEffet || c?.dateEffetContrat);
      default:
        return "-";
    }
  };

  /* ====== Tri ====== */
  function sortDataTable(attribut, order) {
    const mult = order === "asc" ? 1 : -1;
    setDataTable((prev) => {
      const arr = [...(prev || [])];
      arr.sort((A, B) => {
        const a = A?.contract || {};
        const b = B?.contract || {};
        switch (attribut) {
          case "Usage":
          case "Pack":
          case "Police":
          case "Immat.":
            return (
              mult *
              String(getContractField(a, attribut)).localeCompare(
                String(getContractField(b, attribut))
              )
            );
          case "Prime Nette":
            return (
              mult *
              (num(getContractField(a, "Prime Nette")) -
                num(getContractField(b, "Prime Nette")))
            );
          case "Date d'effet":
            return (
              mult *
              ((parseAnyDate(getContractField(a, "Date d'effet"))?.getTime() || 0) -
                (parseAnyDate(getContractField(b, "Date d'effet"))?.getTime() || 0))
            );
          default:
            return 0;
        }
      });
      return arr;
    });
  }

  /* ====== Getters sinistre ====== */
  const getClaimNumber = (cl) => cl?.claimNumber || cl?.numSinistre || "-";
  const getOccurrenceDate = (cl) =>
    fmtDate(cl?.occurrenceDate || cl?.dateSurvenance || cl?.accidentDate || cl?.createdAt);
  const getAdverseCompany = () => "-";
  const getClaimType = (cl) => safe(cl?.damageType || cl?.claimType || cl?.type || "-");
  const getStatus = (cl) => safe(cl?.status || cl?.etatDossier || "-");
  const getReglementList = (cl) => toArray(cl?.reglement || cl?.reglements || cl?.settlements);
  const getDateReglement = (cl) => {
    const regs = getReglementList(cl);
    const found =
      regs.find(
        (r) =>
          r?.typeReglement === "MONTANT_REGELEMENT" || r?.type === "MONTANT_REGELEMENT"
      ) || regs.find((r) => r?.dateReglement);
    return found ? fmtDate(found.dateReglement) : "-";
  };

  return (
    <div style={{ overflowX: "auto", marginBottom: 20 }}>
      <table
        style={{
          width: "100%",
          marginBottom: 20,
          borderRadius: 7,
          tableLayout: "auto",
          borderCollapse: "separate",
          borderSpacing: 0,
        }}
      >
        <thead>
          <tr>
            {td1Headers.map((header, i) => (
              <th
                key={`h1-${i}`}
                style={{
                  color: "rgba(181, 181, 195, 1)",
                  fontSize: 14,
                  fontWeight: "bold",
                  padding: 15,
                  background: "#f7f9fb",
                }}
              >
                <Stack direction="row" alignItems="center" gap={4}>
                  <div>{header}</div>
                  <div
                    style={{ color: "#000", cursor: "pointer" }}
                    onClick={() => sortDataTable(header, "asc")}
                    title="Trier ↑"
                  >
                    <IoMdArrowDropup />
                  </div>
                  <div
                    style={{ color: "#000", cursor: "pointer" }}
                    onClick={() => sortDataTable(header, "dsc")}
                    title="Trier ↓"
                  >
                    <IoMdArrowDropdown />
                  </div>
                </Stack>
              </th>
            ))}
            {td2Headers.map((header, i) => (
              <th
                key={`h2-${i}`}
                style={{
                  color: "rgba(0, 34, 38, 0.64)",
                  fontSize: 14,
                  fontWeight: "bold",
                  padding: 15,
                  background: "#e9eec4",
                }}
              >
                {header}
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {(dataTable || [])
            .map((ligne) => ({
              contract: ligne?.contract || {},
              relatedClaims: Array.isArray(ligne?.relatedClaims)
                ? ligne.relatedClaims
                : [],
            }))
            .filter((ligne) => ligne.relatedClaims.length > 0)
            .map((ligne, idx) => {
              const c = ligne.contract;
              const claimsArr = ligne.relatedClaims;

              return (
                <React.Fragment key={`row-${idx}`}>
                  <tr style={{ borderTop: "1.5px solid #175528" }}>
                    <td style={{ fontSize: 14, textAlign: "center", padding: 15 }} rowSpan={claimsArr.length + 1}>
                      {getContractField(c, "Police")}
                    </td>
                    <td style={{ fontSize: 14, textAlign: "center", padding: 15 }} rowSpan={claimsArr.length + 1}>
                      {getContractField(c, "Pack")}
                    </td>
                    <td style={{ fontSize: 14, textAlign: "center", padding: 15 }} rowSpan={claimsArr.length + 1}>
                      {getContractField(c, "Immat.")}
                    </td>
                    <td style={{ fontSize: 14, textAlign: "center", padding: 15, maxWidth: 160 }} rowSpan={claimsArr.length + 1}>
                      <span
                        title={getContractField(c, "Usage")}
                        style={{
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "inline-block",
                          maxWidth: 150,
                        }}
                      >
                        {(() => {
                          const u = String(getContractField(c, "Usage"));
                          return u.length > 18 ? `${u.slice(0, 18)}…` : u;
                        })()}
                      </span>
                    </td>
                    <td style={{ fontSize: 14, textAlign: "center", padding: 15 }} rowSpan={claimsArr.length + 1}>
                      {(() => {
                        const p = getContractField(c, "Prime Nette");
                        return p === "-" ? "-" : `${Math.round(num(p)).toLocaleString("fr-FR")} DT`;
                      })()}
                    </td>
                    <td style={{ fontSize: 14, textAlign: "center", padding: 15 }} rowSpan={claimsArr.length + 1}>
                      {getContractField(c, "Date d'effet")}
                    </td>
                  </tr>

                  {claimsArr.map((cl, j) => (
                    <tr style={{ borderTop: "1.5px solid #175528" }} key={`cl-${idx}-${j}`}>
                      <td
                        style={{
                          borderBottom: "1px solid #E5EAED",
                          fontSize: 14,
                          textAlign: "center",
                          padding: 15,
                          background: "#fafbee",
                          wordWrap: "break-word",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {getClaimNumber(cl)}
                      </td>
                      <td
                        style={{
                          borderBottom: "1px solid #E5EAED",
                          fontSize: 14,
                          textAlign: "center",
                          padding: 15,
                          background: "#fafbee",
                        }}
                      >
                        {getOccurrenceDate(cl)}
                      </td>
                      <td
                        style={{
                          borderBottom: "1px solid #E5EAED",
                          fontSize: 14,
                          textAlign: "center",
                          padding: 15,
                          background: "#fafbee",
                        }}
                      >
                        {getAdverseCompany(cl)}
                      </td>
                      <td
                        style={{
                          borderBottom: "1px solid #E5EAED",
                          fontSize: 14,
                          textAlign: "center",
                          padding: 15,
                          background: "#fafbee",
                        }}
                      >
                        {getClaimType(cl)}
                      </td>
                      <td
                        style={{
                          borderBottom: "1px solid #E5EAED",
                          fontSize: 14,
                          textAlign: "left",
                          padding: 15,
                          background: "#fafbee",
                        }}
                      >
                        {(() => {
                          const regs = getReglementList(cl);
                          if (regs.length === 0) return "-";
                          return regs.map((r, k) => (
                            <div key={k}>
                              - {safe(r?.causeDommage || r?.damageCause || "N/A")}:{" "}
                              {safe(r?.montant || r?.amount || "-")}
                            </div>
                          ));
                        })()}
                      </td>
                      <td
                        style={{
                          borderBottom: "1px solid #E5EAED",
                          fontSize: 14,
                          textAlign: "left",
                          padding: 15,
                          background: "#fafbee",
                        }}
                      >
                        {getStatus(cl)}
                      </td>
                      <td
                        style={{
                          borderBottom: "1px solid #E5EAED",
                          fontSize: 14,
                          textAlign: "center",
                          padding: 15,
                          background: "#fafbee",
                        }}
                      >
                        {getDateReglement(cl)}
                      </td>
                    </tr>
                  ))}

                  <tr>
                    <td colSpan={td1Headers.length + td2Headers.length} style={{ borderTop: "1px solid #000", padding: 0 }} />
                  </tr>
                </React.Fragment>
              );
            })}
        </tbody>
      </table>
    </div>
  );
}
